import { FC } from 'react';
import { AutoComplete, Spin, Tag, Typography } from 'antd';
import { ProductSearchResult } from '#types/SignifyApi.types';
import FlexCol from '../../../../../components/atoms/FlexCol';

const { Text } = Typography;
const CURRENT_TIME_MS = Date.now();

/**
 * Props for SignifyProductSearch component
 */
interface SignifyProductSearchProps {
  productSearchValue: string;
  productOptions: ProductSearchResult[];
  searchingProducts: boolean;
  loading: boolean;
  onProductSearch: (value: string) => void;
  onProductSelect: (value: string) => void;
  onProductClear: () => void;
}

/**
 * Component for searching and selecting Signify products
 * Provides autocomplete functionality with product details
 */
const SignifyProductSearch: FC<SignifyProductSearchProps> = ({
  productSearchValue,
  productOptions,
  searchingProducts,
  loading,
  onProductSearch,
  onProductSelect,
  onProductClear,
}) => {
  return (
    <FlexCol gap={0}>
      <Text>Search Product</Text>
      <AutoComplete
        aria-label="Search products"
        style={{ width: '100%' }}
        value={productSearchValue}
        options={productOptions.map((product) => {
          const delistmentDateTimestamp = product.delistmentDate
            ? new Date(product.delistmentDate).getTime()
            : null;
          const hasValidDelistmentDate = delistmentDateTimestamp !== null && !Number.isNaN(delistmentDateTimestamp);
          const isDelisted = hasValidDelistmentDate ? delistmentDateTimestamp < CURRENT_TIME_MS : false;

          return {
            value: product.code,
            label: (
              <div>
                <div>
                  {product.name}
                  {product.configurable && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>Configurable</Tag>
                  )}
                  {product.onlinePurchasable && (
                    <Tag color="success" style={{ marginLeft: 8 }}>Available Online</Tag>
                  )}
                  {hasValidDelistmentDate && (
                    <Tag
                      color={isDelisted ? 'error' : 'warning'}
                      style={{ marginLeft: 8 }}
                    >
                      {isDelisted ? 'Product Delisted' : 'Delisting Soon'}
                    </Tag>
                  )}
                </div>
                <Text type="secondary">
                  <small>
                    {product.commercialCode} | 12NC: {product.code}
                  </small>
                </Text>
              </div>
            ),
          };
        })}
        showSearch={{ onSearch: onProductSearch }}
        onSelect={onProductSelect}
        onChange={(value) => {
          if (!value) {
            onProductClear();
          }
        }}
        disabled={loading}
        placeholder="Type three letters or more..."
        allowClear
        notFoundContent={searchingProducts ? <Spin size="small" /> : 'No products found'}
      />
    </FlexCol>
  );
};

export default SignifyProductSearch;